import * as React from "react";
import { ChevronDown, X } from "lucide-react";

import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Badge } from "@/components/ui/badge";

export interface MultiSelectOption {
  value: string;
  label: string;
  group?: string;
  groupLabel?: string;
}

export interface MultiSelectSection {
  id: string;
  label: string;
  description?: string;
  options: MultiSelectOption[];
}

interface MultiSelectProps {
  sections: MultiSelectSection[];
  selected: string[];
  onSelect: (value: string) => void;
  onSelectAll: (sectionId: string) => void;
  onDeselectAll: (sectionId: string) => void;
  onRemove: (value: string) => void;
  onOpenChange?: (open: boolean) => void;
  placeholder?: string;
  disabled?: boolean;
  triggerWidth?: string;
  dropdownWidth?: string;
  maxHeight?: string;
  columns?: number | "auto-fit";
  optionMinWidth?: string;
  badgeLimit?: number;
  showSelectedBadges?: boolean;
  dropdownFooter?: React.ReactNode;
  wrapOptionLabels?: boolean;
  triggerAriaLabel?: string;
  triggerLabel?: string;
  triggerIcon?: React.ReactNode;
  triggerClassName?: string;
  iconOnlyTrigger?: boolean;
}

export function MultiSelect({
  sections,
  selected,
  onSelect,
  onSelectAll,
  onDeselectAll,
  onRemove,
  onOpenChange,
  placeholder = "Select items...",
  disabled = false,
  triggerWidth = "w-[250px]",
  dropdownWidth = "w-[500px]",
  maxHeight = "max-h-[400px]",
  columns = 4,
  optionMinWidth = "12rem",
  badgeLimit = 20,
  showSelectedBadges = true,
  dropdownFooter,
  wrapOptionLabels = false,
  triggerAriaLabel,
  triggerLabel,
  triggerIcon,
  triggerClassName,
  iconOnlyTrigger = false,
}: MultiSelectProps) {
  const [open, setOpen] = React.useState(false);

  React.useEffect(() => {
    if (!disabled || !open) {
      return;
    }

    setOpen(false);
    onOpenChange?.(false);
  }, [disabled, open, onOpenChange]);

  const handleOpenChange = (newOpen: boolean) => {
    setOpen(newOpen);
    onOpenChange?.(newOpen);
  };

  const selectedCount = selected.length;
  const resolvedTriggerLabel = React.useMemo(
    () => triggerLabel ?? placeholder.replace(/\.\.\.$/, ""),
    [placeholder, triggerLabel]
  );

  const selectedItems = React.useMemo(() => {
    const items: Array<{ value: string; label: string }> = [];
    for (const value of selected) {
      for (const section of sections) {
        const option = section.options.find((opt) => opt.value === value);
        if (option) {
          items.push({ value: option.value, label: option.label });
          break;
        }
      }
    }
    return items;
  }, [selected, sections]);

  const handleOptionToggle = (value: string, isSelected: boolean) => {
    if (isSelected) {
      onRemove(value);
    } else {
      onSelect(value);
    }
  };
  const numericColumns = typeof columns === "number" ? columns : null;
  const triggerText =
    selectedCount === 0 ? placeholder : `${resolvedTriggerLabel} · ${selectedCount}`;

  return (
    <div className="space-y-2">
      <Popover open={open} onOpenChange={handleOpenChange}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            aria-label={triggerAriaLabel ?? (iconOnlyTrigger ? resolvedTriggerLabel : undefined)}
            className={cn(
              "h-8 justify-between gap-2 rounded-md border border-[#333] bg-background px-2.5 text-xs font-medium shadow-none hover:bg-muted/50",
              triggerWidth,
              triggerClassName
            )}
            disabled={disabled}
          >
            {iconOnlyTrigger ? (
              <span className="shrink-0 text-muted-foreground">{triggerIcon}</span>
            ) : (
              <>
                <span className="flex min-w-0 items-center gap-2">
                  {triggerIcon ? (
                    <span className="shrink-0 text-muted-foreground">{triggerIcon}</span>
                  ) : null}
                  <span className="truncate text-left">{triggerText}</span>
                </span>
                <ChevronDown className="h-3.5 w-3.5 shrink-0 opacity-60" />
              </>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className={cn("border border-[#333] bg-popover/98 p-0 shadow-lg", dropdownWidth)}
          align="start"
        >
          <div className="flex flex-col">
            <div className={cn("space-y-3 overflow-auto p-3", maxHeight)}>
              {sections.map((section) => {
                const sectionSelected = section.options.filter((opt) =>
                  selected.includes(opt.value)
                );
                const allSelected =
                  section.options.length > 0 &&
                  sectionSelected.length === section.options.length;
                const someSelected =
                  sectionSelected.length > 0 && !allSelected;

                return (
                  <div key={section.id} className="space-y-2">
                    <div className="flex items-center justify-between gap-2 border-b border-[#333] pb-2">
                      <div className="flex min-w-0 items-center gap-2">
                        <Checkbox
                          checked={
                            allSelected ? true : someSelected ? "indeterminate" : false
                          }
                          onCheckedChange={(checked) => {
                            if (checked) {
                              onSelectAll(section.id);
                            } else {
                              onDeselectAll(section.id);
                            }
                          }}
                        />
                        <span className="truncate text-xs font-semibold uppercase tracking-[0.14em]">
                          {section.label}
                        </span>
                        {section.description && (
                          <span className="truncate text-[10px] text-muted-foreground">
                            {section.description}
                          </span>
                        )}
                      </div>
                      <div className="flex gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 border border-[#333] px-2 text-[10px] font-medium uppercase tracking-[0.12em] hover:bg-muted/60"
                          onClick={() => onSelectAll(section.id)}
                        >
                          All
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 border border-[#333] px-2 text-[10px] font-medium uppercase tracking-[0.12em] hover:bg-muted/60"
                          onClick={() => onDeselectAll(section.id)}
                        >
                          Clear
                        </Button>
                      </div>
                    </div>
                    <div
                      style={
                        columns === "auto-fit"
                          ? {
                              gridTemplateColumns: `repeat(auto-fit, minmax(${optionMinWidth}, 1fr))`,
                            }
                          : undefined
                      }
                      className={cn(
                        "grid gap-2",
                        numericColumns === 1 && "grid-cols-1",
                        numericColumns === 2 && "grid-cols-2",
                        numericColumns === 3 && "grid-cols-3",
                        numericColumns === 4 && "grid-cols-4",
                        numericColumns === 5 && "grid-cols-5",
                        numericColumns !== null && numericColumns >= 6 && "grid-cols-6"
                      )}
                    >
                      {section.options.map((option) => {
                        const isSelected = selected.includes(option.value);
                        return (
                          <label
                            key={option.value}
                            className={cn(
                              "flex min-h-8 cursor-pointer items-center gap-2 rounded-md border border-[#333] bg-background/70 px-2 py-1 text-xs transition-colors",
                              wrapOptionLabels && "items-start py-2",
                              isSelected
                                ? "border-primary/40 bg-primary/10"
                                : "hover:bg-muted/60"
                            )}
                          >
                            <Checkbox
                              checked={isSelected}
                              onCheckedChange={() => handleOptionToggle(option.value, isSelected)}
                            />
                            <span
                              className={cn(
                                "min-w-0",
                                wrapOptionLabels
                                  ? "whitespace-normal break-words leading-snug"
                                  : "truncate"
                              )}
                            >
                              {option.label}
                            </span>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
            {dropdownFooter ? (
              <div className="border-t border-[#333] px-3 py-3">{dropdownFooter}</div>
            ) : null}
          </div>
        </PopoverContent>
      </Popover>

      {showSelectedBadges && selectedItems.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {selectedItems.slice(0, badgeLimit).map((item) => (
            <Badge
              key={item.value}
              variant="secondary"
              className="flex items-center gap-1 pr-1"
            >
              {item.label}
              <button
                type="button"
                onClick={() => onRemove(item.value)}
                disabled={disabled}
                aria-label={`Remove ${item.label}`}
                className="ml-1 rounded-sm opacity-70 hover:opacity-100 disabled:opacity-50"
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          ))}
          {selectedItems.length > badgeLimit && (
            <Badge variant="secondary">
              +{selectedItems.length - badgeLimit} more
            </Badge>
          )}
        </div>
      )}
    </div>
  );
}
